import { useEffect, useState } from 'react';

/**
 * Sticky A/B variant picker.
 *
 *  - Picks a random variant on the user's first visit (uniform distribution).
 *  - Persists the choice in localStorage so the same visitor always sees the
 *    same variant on return (avoids the "different headline every refresh"
 *    confusion).
 *  - Allows preview-time override via `?v=variant-id` URL parameter (great for
 *    sharing a specific variant with a partner without flipping localStorage).
 *  - Exposes the chosen variant id on `window.__abVariant` and as a
 *    `data-ab-variant` attribute on document.body so analytics can attribute
 *    conversions per variant later.
 *
 * Usage:
 *   const variant = useABVariant('hero', heroVariants);
 *   return <h1>{variant.line1}<br/>{variant.line2}</h1>;
 */
export const useABVariant = (key, variants) => {
  const lsKey = `cc_ab_${key}_variant`;

  const pick = () => {
    if (typeof window === 'undefined') return variants[0];

    // 1. URL override — `?v=anti-excel`
    const params = new URLSearchParams(window.location.search);
    const urlV = params.get('v');
    const fromUrl = urlV ? variants.find((v) => v.id === urlV) : null;
    if (fromUrl) return fromUrl;

    // 2. localStorage stickiness
    try {
      const saved = localStorage.getItem(lsKey);
      if (saved) {
        const found = variants.find((v) => v.id === saved);
        if (found) return found;
      }
    } catch {
      /* ignore — private mode */
    }

    // 3. First visit — pick uniformly at random and persist
    const fresh = variants[Math.floor(Math.random() * variants.length)];
    try {
      localStorage.setItem(lsKey, fresh.id);
    } catch {
      /* ignore */
    }
    return fresh;
  };

  const [variant] = useState(pick);

  // Side-effects after mount: stamp body attribute + expose for analytics
  useEffect(() => {
    if (!variant) return;
    if (typeof document !== 'undefined') {
      document.body.setAttribute(`data-ab-${key}`, variant.id);
    }
    if (typeof window !== 'undefined') {
      window.__abVariant = window.__abVariant || {};
      window.__abVariant[key] = variant.id;
    }
  }, [key, variant]);

  return variant;
};
